/**
 * Intensity Engine — pure computation module for heatmap visualization.
 * Calculates NPS (notes per second), physical strain, per-note difficulty,
 * and maps everything to a blue-green-yellow-red color gradient.
 * No React, no hooks — just math + typed arrays.
 */

import type { Note } from '../types/beatmap';
import { GRID_SCALE } from '../types/beatmap';

// ── Types ────────────────────────────────────────────────────────────────────

export interface IntensityData {
  /** 0..1 normalized difficulty per note */
  noteIntensity: Float32Array;
  /** Hex color string per note (heatmap gradient) */
  noteColors: string[];
  /** NPS sampled at each integer beat (windowed) */
  beatNPS: Float32Array;
  /** Physical strain sampled at each integer beat */
  beatStrain: Float32Array;
  /** Combined intensity per beat: 0.6*NPS + 0.4*strain, normalized 0..1 */
  beatIntensity: Float32Array;
  /** Total number of beats in the map */
  totalBeats: number;
  /** Statistics */
  peakNPS: number;
  avgNPS: number;
  peakStrain: number;
}

// ── Constants ────────────────────────────────────────────────────────────────

/** Half-window in beats for smoothing (2 beats each side = ~1s at 120 BPM) */
const WINDOW_HALF_BEATS = 2;

/** Grid scale multiplied by 10 to match 3D scene coordinates */
const WORLD_SCALE = GRID_SCALE * 10;

/** Color gradient stops: blue → cyan → green → amber → red */
const COLOR_STOPS: { t: number; r: number; g: number; b: number }[] = [
  { t: 0.00, r: 0x3B, g: 0x82, b: 0xF6 },  // #3B82F6 blue
  { t: 0.25, r: 0x06, g: 0xB6, b: 0xD4 },  // #06B6D4 cyan
  { t: 0.50, r: 0x22, g: 0xC5, b: 0x5E },  // #22C55E green
  { t: 0.75, r: 0xF5, g: 0x9E, b: 0x0B },  // #F59E0B amber
  { t: 1.00, r: 0xEF, g: 0x44, b: 0x44 },  // #EF4444 red
];

// ── Color Mapping ────────────────────────────────────────────────────────────

/** Convert an intensity value (0..1) to a hex color on the heatmap gradient. */
export function intensityToColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));

  // Find the two stops to interpolate between
  let lo = COLOR_STOPS[0];
  let hi = COLOR_STOPS[COLOR_STOPS.length - 1];
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (clamped >= COLOR_STOPS[i].t && clamped <= COLOR_STOPS[i + 1].t) {
      lo = COLOR_STOPS[i];
      hi = COLOR_STOPS[i + 1];
      break;
    }
  }

  const range = hi.t - lo.t;
  const f = range > 0 ? (clamped - lo.t) / range : 0;

  const r = Math.round(lo.r + (hi.r - lo.r) * f);
  const g = Math.round(lo.g + (hi.g - lo.g) * f);
  const b = Math.round(lo.b + (hi.b - lo.b) * f);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Convert intensity (0..1) to RGB components (0..1 range for Three.js).
 * Avoids string allocation for per-frame vertex color updates.
 */
export function intensityToRGB(t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));

  let lo = COLOR_STOPS[0];
  let hi = COLOR_STOPS[COLOR_STOPS.length - 1];
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (clamped >= COLOR_STOPS[i].t && clamped <= COLOR_STOPS[i + 1].t) {
      lo = COLOR_STOPS[i];
      hi = COLOR_STOPS[i + 1];
      break;
    }
  }

  const range = hi.t - lo.t;
  const f = range > 0 ? (clamped - lo.t) / range : 0;

  return [
    (lo.r + (hi.r - lo.r) * f) / 255,
    (lo.g + (hi.g - lo.g) * f) / 255,
    (lo.b + (hi.b - lo.b) * f) / 255,
  ];
}

// ── Binary Search Helpers ────────────────────────────────────────────────────

/** Find index of first note with time_beats >= target */
function lowerBound(beats: Float32Array, target: number): number {
  let lo = 0, hi = beats.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (beats[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Find index of first note with time_beats > target */
function upperBound(beats: Float32Array, target: number): number {
  let lo = 0, hi = beats.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (beats[mid] <= target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// ── Main Computation ─────────────────────────────────────────────────────────

/**
 * Compute all intensity data for a set of notes at a given BPM.
 * This is the main entry point — call once when beatmap/difficulty changes.
 */
export function computeIntensity(notes: Note[], bpm: number): IntensityData {
  const n = notes.length;

  if (n === 0) {
    return {
      noteIntensity: new Float32Array(0),
      noteColors: [],
      beatNPS: new Float32Array(0),
      beatStrain: new Float32Array(0),
      beatIntensity: new Float32Array(0),
      totalBeats: 0,
      peakNPS: 0,
      avgNPS: 0,
      peakStrain: 0,
    };
  }

  // ── Sort notes by time_beats (should already be sorted, but ensure it) ──
  const sorted = [...notes].sort((a, b) => a.time_beats - b.time_beats);
  const noteBeatTimes = new Float32Array(n);
  for (let i = 0; i < n; i++) noteBeatTimes[i] = sorted[i].time_beats;

  const maxBeat = Math.ceil(noteBeatTimes[n - 1]);
  const totalBeats = Math.max(maxBeat + 1, 1);
  const secondsPerBeat = 60 / bpm;

  // ── 1. Per-beat NPS (windowed) ──────────────────────────────────────────
  const beatNPS = new Float32Array(totalBeats);
  const windowDurationSec = WINDOW_HALF_BEATS * 2 * secondsPerBeat;

  for (let b = 0; b < totalBeats; b++) {
    const winStart = b - WINDOW_HALF_BEATS;
    const winEnd = b + WINDOW_HALF_BEATS;
    const lo = lowerBound(noteBeatTimes, winStart);
    const hi = upperBound(noteBeatTimes, winEnd);
    beatNPS[b] = (hi - lo) / windowDurationSec;
  }

  // ── 2. Per-note physical strain ─────────────────────────────────────────
  const noteStrain = new Float32Array(n);
  for (let i = 1; i < n; i++) {
    const dx = (sorted[i].position[0] - sorted[i - 1].position[0]) * WORLD_SCALE;
    const dy = (sorted[i].position[1] - sorted[i - 1].position[1]) * WORLD_SCALE;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const dt = Math.max(sorted[i].time_seconds - sorted[i - 1].time_seconds, 0.01);
    const speed = distance / dt;
    noteStrain[i] = speed * (1 + distance * 0.5);
  }

  // ── 3. Per-beat strain (windowed average) ───────────────────────────────
  const beatStrain = new Float32Array(totalBeats);
  for (let b = 0; b < totalBeats; b++) {
    const winStart = b - WINDOW_HALF_BEATS;
    const winEnd = b + WINDOW_HALF_BEATS;
    const lo = lowerBound(noteBeatTimes, winStart);
    const hi = upperBound(noteBeatTimes, winEnd);
    if (hi > lo) {
      let sum = 0;
      for (let j = lo; j < hi; j++) sum += noteStrain[j];
      beatStrain[b] = sum / (hi - lo);
    }
  }

  // ── 4. Normalize NPS and strain to 0..1 ────────────────────────────────
  let peakNPS = 0, sumNPS = 0;
  for (let b = 0; b < totalBeats; b++) {
    if (beatNPS[b] > peakNPS) peakNPS = beatNPS[b];
    sumNPS += beatNPS[b];
  }
  const avgNPS = totalBeats > 0 ? sumNPS / totalBeats : 0;

  let peakStrain = 0;
  for (let b = 0; b < totalBeats; b++) {
    if (beatStrain[b] > peakStrain) peakStrain = beatStrain[b];
  }

  // Normalized versions for combining
  const npsNorm = new Float32Array(totalBeats);
  const strainNorm = new Float32Array(totalBeats);
  for (let b = 0; b < totalBeats; b++) {
    npsNorm[b] = peakNPS > 0 ? beatNPS[b] / peakNPS : 0;
    strainNorm[b] = peakStrain > 0 ? beatStrain[b] / peakStrain : 0;
  }

  // ── 5. Combined beat intensity ──────────────────────────────────────────
  const beatIntensity = new Float32Array(totalBeats);
  for (let b = 0; b < totalBeats; b++) {
    beatIntensity[b] = 0.6 * npsNorm[b] + 0.4 * strainNorm[b];
  }

  // ── 6. Per-note intensity & colors ──────────────────────────────────────
  // Build a map from original note index to sorted index for color assignment
  const noteIntensity = new Float32Array(n);
  const noteColors: string[] = new Array(n);

  // We need to map back to original note order. Create a lookup by note_id.
  const sortedIntensity = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const beat = noteBeatTimes[i];
    const b = Math.min(Math.floor(beat), totalBeats - 1);
    const localNPS = peakNPS > 0 ? beatNPS[Math.max(0, b)] / peakNPS : 0;
    // Normalize note-level strain relative to peak
    const localStrain = peakStrain > 0 ? noteStrain[i] / peakStrain : 0;
    sortedIntensity[i] = Math.min(1, 0.6 * localNPS + 0.4 * localStrain);
  }

  // Map results back to original note order
  // Build a reverse mapping: for each original note, find its sorted index
  const originalIndices = new Map<string, number>();
  for (let i = 0; i < notes.length; i++) {
    originalIndices.set(notes[i].note_id, i);
  }

  for (let si = 0; si < n; si++) {
    const origIdx = originalIndices.get(sorted[si].note_id);
    if (origIdx !== undefined) {
      noteIntensity[origIdx] = sortedIntensity[si];
      noteColors[origIdx] = intensityToColor(sortedIntensity[si]);
    }
  }

  // Fill any unmapped (shouldn't happen, but be safe)
  for (let i = 0; i < n; i++) {
    if (!noteColors[i]) {
      noteColors[i] = intensityToColor(0);
      noteIntensity[i] = 0;
    }
  }

  return {
    noteIntensity,
    noteColors,
    beatNPS,
    beatStrain,
    beatIntensity,
    totalBeats,
    peakNPS,
    avgNPS,
    peakStrain,
  };
}
